import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Formik, FormikHelpers } from "formik";
import React, { useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import AuthApi from "../../api/AuthApi";
import { appConstants } from "../../config/app-constants";
import { EToastvariant, ILoginForm } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { setLoggedInUser } from "../../redux/slice/LoggedInUserSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { StorageService, UtilService } from "../../services";
import FASvgIcon from "../FASvgIcon";
import ScrollToFirstError from "../ScrollToFirstError";
import { LongContentModal } from "../modals";
import ButtonSpinner from "../placeholders/ButtonSpinner";
import GoogleSignInBtn from "./GoogleSignInBtn";

const initialValues: ILoginForm = {
  email: "",
  password: "",
  deviceToken: ""
};

const validationSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .matches(appConstants.EMAIL_REGEX, "Invalid email address")
    .required("Email is required"),
  password: Yup.string().trim().required("Password is required")
});

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const redirectTo = queryParams.get("redirect");

  const [showPassword, setShowPassword] = useState(false);
  const [showTermsOfServiceModal, setShowTermsOfServiceModal] = useState<boolean>(false);
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState<boolean>(false);

  const handleLoginForm = async (
    values: ILoginForm,
    { setSubmitting }: FormikHelpers<ILoginForm>
  ) => {
    try {
      setSubmitting(true);

      const _userDeviceToken = await StorageService.getUserDeviceToken();
      _userDeviceToken ? (values.deviceToken = _userDeviceToken) : (values.deviceToken = "");

      const authApi = AuthApi.getInstance();
      const loginRes = await authApi.login({
        ...values,
        email: values.email.toLowerCase()
      });

      if (loginRes.isVerified === true) {
        await StorageService.setApiToken(loginRes.authToken);
        await StorageService.setTermsAccepted(loginRes.user.termsAcceptedAt);
        await StorageService.removePrePostVerifyData();

        dispatch(setLoggedInUser(loginRes.user));

        // check it came from marketing plans screen
        const isPublicPlanUUID = await StorageService.getMarketPlansUrl();
        if (isPublicPlanUUID) {
          navigate("/itr-filings", { replace: true });
        } else {
          if (redirectTo) {
            navigate(`/${redirectTo}`);
          } else {
            navigate("/home", { replace: true });
          }
        }

        dispatch(
          showToastMsg({
            isVisible: true,
            title: "Success",
            message: "Login successfully",
            variant: EToastvariant.Success
          })
        );
      } else {
        await StorageService.setPreVerifyData(
          JSON.stringify({
            email: values.email.toLowerCase(),
            preVerificationToken: loginRes.preVerificationToken
          })
        );
        dispatch(
          showToastMsg({
            isVisible: true,
            title: "Success",
            message: "OTP sent successfully",
            variant: EToastvariant.Success
          })
        );
        navigate("/verification", {
          state: { fromScreen: "login", showVerification: true }
        });
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Container>
        <div className="auth-form-wrap">
          <Row className="align-items-center justify-content-md-center">
            <Col>
              <Card bg="white" className="auth-card">
                <Card.Body>
                  <h1 className="fw-bold text-center mb-1">Sign In</h1>
                  <p className="text-center text-greyDark">to continue to tax filing dashboard</p>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleLoginForm}
                  >
                    {({
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      validateForm,
                      values,
                      errors,
                      isValid,
                      touched,
                      submitCount,
                      isSubmitting
                    }) => (
                      <Form
                        onSubmit={async (event) => {
                          event.preventDefault();
                          await validateForm();
                          handleSubmit();
                        }}
                      >
                        <ScrollToFirstError
                          submitCount={submitCount}
                          isValid={isValid}
                          errors={errors}
                        />
                        <GoogleSignInBtn buttonTitle={"Sign In with Google"} />
                        <Form.Group controlId="email" className="form-group">
                          <Form.Label className="required-field">Email</Form.Label>
                          <Form.Control
                            type="email"
                            autoComplete="false"
                            placeholder="Please enter your email"
                            name="email"
                            value={values.email}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isInvalid={Boolean(errors.email) && touched.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="password" className="form-group">
                          <Form.Label className="required-field">Password</Form.Label>
                          <div className="field-has-icon">
                            <Form.Control
                              type={showPassword ? "text" : "password"}
                              autoComplete="false"
                              placeholder="***********"
                              name="password"
                              value={values.password}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={Boolean(errors.password) && touched.password}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.password}
                            </Form.Control.Feedback>
                            <Button
                              variant="transparent"
                              className="field-icon"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              <FASvgIcon icon={showPassword ? faEye : faEyeSlash} />
                            </Button>
                          </div>
                        </Form.Group>

                        <div className="btn-wrap">
                          <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            className="w-100 shadow"
                            disabled={isSubmitting}
                          >
                            {!isSubmitting ? "Login" : <ButtonSpinner />}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>

              <div className="auth-footer-1">
                <Form.Text className="m-0">
                  <Button variant="link" onClick={() => navigate("/forgot-password")}>
                    Forgot Password?
                  </Button>
                </Form.Text>

                <Button variant="outline-greyDark" size="sm" onClick={() => navigate("/register")}>
                  Sign Up
                </Button>
              </div>
              <div className="auth-footer-2">
                <Form.Text>
                  <Button variant="link" onClick={() => setShowPrivacyPolicyModal(true)}>
                    Privacy Policy
                  </Button>
                </Form.Text>
                <Form.Text>
                  <Button variant="link" onClick={() => setShowTermsOfServiceModal(true)}>
                    Terms of use
                  </Button>
                </Form.Text>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <LongContentModal
        showModal={showTermsOfServiceModal}
        title="Terms of Use"
        onYesBtnText="Close"
        onYesBtnClick={() => {
          setShowTermsOfServiceModal(false);
        }}
        actionKey={"termsOfService"}
      />

      <LongContentModal
        showModal={showPrivacyPolicyModal}
        title="Privacy Policy"
        onYesBtnText="Close"
        onYesBtnClick={() => {
          setShowPrivacyPolicyModal(false);
        }}
        actionKey={"privacyPolicy"}
      />
    </>
  );
};

export default LoginForm;
